import { DepositMethod, WithdrawMethod } from '../../../types';
import { formatNumber } from '../../../utils';

type PaymentMethod = DepositMethod | WithdrawMethod;

interface PaymentCategoryProps {
  title: string;
  methods: Record<string, PaymentMethod[]>;
  onMethodSelect?: (method: PaymentMethod) => void;
  onMoreClick?: (displayedCount: number) => void;
  selectedMethodId?: string | number;
}

function channelIdEq(a: string | number | undefined | null, b: string | number | undefined | null) {
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
}

// show max 3 counts methods
const MAX_DISPLAY = 4;
export default function PaymentCategory(props: PaymentCategoryProps) {
  const flatMethods = createMemo(() => {
    return Object.values(props.methods)
      .map((list) => list?.[0])
      .filter(Boolean) as PaymentMethod[];
  });

  const showMoreMethods = createMemo(() => flatMethods().length > MAX_DISPLAY);

  /** show 3 counts methods */
  const displayMethods = createMemo(() => {
    const flat = flatMethods();
    if (flat.length <= MAX_DISPLAY) return flat;
    return flat.slice(0, MAX_DISPLAY - 1);
  });

  /** More Methods selected method */
  const extraSelected = createMemo((): PaymentMethod | null => {
    if (!showMoreMethods()) return null;
    const selId = props.selectedMethodId;
    if (selId == null) return null;
    if (displayMethods().some((m) => channelIdEq(m.channelId, selId))) return null;
    return flatMethods().find((m) => channelIdEq(m.channelId, selId)) ?? null;
  });

  return (
    <div class="flex flex-col gap-2">
      <h2 class="px-1 text-sm font-semibold text-neutral-main3">{props.title}</h2>

      <div class={`grid gap-3 ${displayMethods().length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <For each={displayMethods()}>
          {(method, idx) => (
            <div class="relative">
              <button
                type="button"
                onClick={() => props.onMethodSelect?.(method)}
                class={`flex h-12 w-full items-center gap-2 rounded-lg px-1.5 py-1.5 transition-colors ${
                  channelIdEq(props.selectedMethodId, method.channelId)
                    ? 'border border-brand bg-layer4-card'
                    : ' bg-layer5 hover:bg-layer4'
                }`}
              >
                <div class="flex h-9 w-16 flex-shrink-0 items-center justify-center rounded-md bg-layer3-card">
                  {method.lightIconUrl && (
                    <img src={method.lightIconUrl} alt={method.displayName} class="h-full w-full object-contain" />
                  )}
                </div>

                <div class="flex min-w-0 flex-1 flex-col items-end justify-between gap-0.5 overflow-hidden">
                  <p
                    class="w-full truncate text-right text-sm font-semibold text-neutral-main1"
                    title={method.displayName}
                  >
                    {method.displayName}
                  </p>
                  <p
                    class="w-full truncate text-right text-xs font-normal text-neutral-main2"
                    title={`${formatNumber(method.minLimit)} ～ ${formatNumber(method.maxLimit)}`}
                  >
                    {formatNumber(method.minLimit)} ～ {formatNumber(method.maxLimit)}
                  </p>
                </div>
              </button>

              <Show when={idx() === 0 && (method.tag === 0 || method.tag === 1)}>
                <div
                  class={cx(
                    'center absolute right-1 -top-1 translate-x-1 -translate-y-1/2 rounded-[4px] px-1 h-[18px] backdrop-blur-sm',
                    method.tag === 1 ? 'bg-alpha-brand-brand-a10' : 'bg-alpha-function-orange-a10'
                  )}
                >
                  <span
                    class={cx(
                      'caption-s-regular',
                      method.tag === 1 ? 'text-function-success' : 'text-function-highlight'
                    )}
                  >
                    {method.tag === 1 ? t('Recommend') : t('Last Used')}
                  </span>
                </div>
              </Show>
            </div>
          )}
        </For>

        <Show when={showMoreMethods() && props.onMoreClick}>
          <button
            type="button"
            onClick={() => props.onMoreClick?.(displayMethods().length)}
            class="flex h-12 w-full items-center justify-between rounded-lg border border-main2 bg-layer5 px-3 py-1.5 transition-colors hover:bg-layer4"
          >
            <div class="flex items-center gap-1">
              <IconViewAll class="size-[18px] text-neutral-main1_alt" />
              <span class="text-base font-semibold text-neutral-main1">{t('More Methods')}</span>
            </div>
            <IconArrowStyle02 class="size-4 text-neutral-main1" aria-hidden="true" />
          </button>
        </Show>
      </div>

      <Show when={extraSelected()}>
        {(method) => (
          <button
            type="button"
            onClick={() => props.onMethodSelect?.(method())}
            class="flex h-12 w-full items-center gap-2 rounded-lg border-2 border-brand bg-layer4-card px-1.5 py-1.5 transition-colors"
          >
            <div class="flex h-9 w-16 flex-shrink-0 items-center justify-center rounded-md bg-layer3-card">
              {method().lightIconUrl && (
                <img src={method().lightIconUrl} alt={method().displayName} class="h-full w-full object-contain" />
              )}
            </div>
            <div class="flex min-w-0 flex-1 flex-col items-end justify-between gap-0.5 overflow-hidden">
              <p
                class="w-full truncate text-right text-sm font-semibold text-neutral-main1"
                title={method().displayName}
              >
                {method().displayName}
              </p>
              <p
                class="w-full truncate text-right text-xs font-normal text-neutral-main2"
                title={`${formatNumber(method().minLimit)} ～ ${formatNumber(method().maxLimit)}`}
              >
                {formatNumber(method().minLimit)} ～ {formatNumber(method().maxLimit)}
              </p>
            </div>
          </button>
        )}
      </Show>
    </div>
  );
}
